import { BaseModelType } from "../BaseModel";
import { VariablePrototype } from "../bridge";

// ─── Element-level config types ───────────────────────────────────────────────

/**
 * Config for LEVER-role elements on TOOL blueprints.
 * Controls the semantic intent of this specific lever input — used by surface
 * renderers to apply appropriate labels/framing (e.g., "Your goal" vs "Benchmark").
 * The surface-level input_framing setting in SimulatorSurfaceConfig acts as a
 * fallback when this is not set.
 */
export type ToolElementConfig = {
    /** Semantic intent for this specific LEVER input. */
    input_intent?: ToolElementFraming;
    /** How a LEVER input is presented; ignored for other roles. */
    input_mode?: ToolInputMode;
    /** Whether a value must be provided before calculation can be triggered. */
    // is_required?: boolean;
};
export type ToolElementFraming = "NEUTRAL" | "ACTUAL" | "TARGET";
export type ToolInputMode = "SLIDER" | "SLIDER_WITH_PRESETS";

/**
 * BlueprintElement: declares which variable prototype fills a given semantic role in a blueprint,
 * plus any per-element config (display, trigger, dependency behavior, etc.).
 *
 * Meaningful for TOOL / THEME / ANALYSIS / EVALUATION blueprints, where element roles carry
 * semantic weight (LEVER vs OUTPUT, SIGNAL vs FOCUS, etc.) and prototype linkage enables
 * cross-source identity (model variable ↔ metric variable via shared prototype).
 *
 * Also applicable to FORM blueprints for NUMERICAL_SLIDER questions: QUESTION-role elements
 * link a slider variable to a canonical prototype, enabling cross-source identity with metric
 * variables. Non-numerical form variables do not need elements — the context assignment alone
 * is sufficient for workspace participation.
 */
export type BlueprintElement = BaseModelType & {
    module_blueprint_id: string;
    variable_prototype_id?: string; // semantic anchor — links element to a canonical concept
    element_role: BlueprintElementRole;
    group_key?: string | null;
    weight: number;
    config?: BlueprintElementConfig | null;

    variablePrototype?: VariablePrototype;
    // tbd:
    // properties about hydration sources, exposure, priority, featuring, reporting,
    // default settings, requests, scope/time/grain specs
    // (like original idea about view-specific behavior via MV configs: interactive, workspace, insight)
};

export enum BlueprintElementRole {
    // ─────────────────────────────────────────────────────────────
    // THEME
    // ─────────────────────────────────────────────────────────────
    /** A monitored signal related to the theme */
    SIGNAL = "SIGNAL",
    /** Primary anchor metric or concept (if any are foregrounded) */
    FOCUS = "FOCUS",
    /** Descriptive metric with auxiliary role in explanation/recommendation */
    CONTEXT = "CONTEXT",

    // ─────────────────────────────────────────────────────────────
    // TOOL
    // ─────────────────────────────────────────────────────────────
    /** Primary independent variable — user-controlled input (main lever) */
    LEVER = "LEVER",
    /** Secondary independent variable — user-controlled input (supplementary) */
    PARAMETER = "PARAMETER",
    /** Calculated intermediate — computed but not a primary output of focus */
    INTERMEDIATE = "INTERMEDIATE",
    /** Modeled or projected output under scenario state — key calculated result */
    OUTPUT = "OUTPUT",

    // ─────────────────────────────────────────────────────────────
    // ANALYSIS
    // ─────────────────────────────────────────────────────────────
    /** Supporting or explanatory metric (often causal) */
    DRIVER = "DRIVER",
    /** Metric as limiting constraint/contributor/inhibitor */
    FACTOR = "FACTOR",
    /** Metric framed as effect/consequence/indicator */
    OUTCOME = "OUTCOME",

    // ─────────────────────────────────────────────────────────────
    // EVALUATION
    // ─────────────────────────────────────────────────────────────
    /** Thing being evaluated/measured */
    SUBJECT = "SUBJECT",
    /** Evaluation result artifact (score, rating, grade, index) */
    POSITION = "POSITION",
    /** Supporting values used to interpret main subject/score */
    EVIDENCE = "EVIDENCE",
    // possibly a role for the main element: index/synthesis/aggregate

    // ─────────────────────────────────────────────────────────────
    // FORM
    // ─────────────────────────────────────────────────────────────
    /** Generic prototype-linked form question; cross-source identity without goal commitment */
    QUESTION = "QUESTION",
    /** Goal-setting question; submitted value is written to metric_values with intent=GOAL */
    GOAL = "GOAL",
}

/** Generic per-element config applicable to any blueprint element. */
export type BlueprintElementConfig = {
    /** Whether this element's prototype is used as the headline value on its orchestrator item. */
    is_headline?: boolean;
};
